import { useEffect, useState, useRef } from "react"
import axios from "axios"
import { useSelector } from "react-redux"

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_BASE_URL

const ProfileSettings = () => {
  const accessToken = useSelector((state) => state.auth.access)
  const fileInputRef = useRef(null)
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    userName: "",
    phoneNumber: "",
    skillOccupation: "",
    bio: "",
    displayName: "",
    profilePhoto: "", // Add profile photo URL
    //timezone: "Karachi",
  })
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)

  useEffect(() => {
    axios.get(`${BASE_URL}/users/profile/`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    }).then((res) => {
      const data = res.data
      setProfileData({
        firstName: data.first_name || "",
        lastName: data.last_name || "",
        userName: data.username || "",
        phoneNumber: data.contact || "",
        skillOccupation: data.occupation || "",
        bio: data.bio || "",
        displayName: `${data.first_name} ${data.last_name}`,
        profilePhoto: data.profile_photo || "", 
        //timezone: "Karachi",
      })
      
      // Set initial photo preview if profile photo exists
      if (data.profile_photo) {
        // Construct full URL if it's a relative path
        const photoUrl = data.profile_photo.startsWith('http') 
          ? data.profile_photo 
          : `${BASE_URL}${data.profile_photo}`
        setPhotoPreview(photoUrl)
      }
    })
  }, [accessToken])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setProfileData((prev) => ({ ...prev, [name]: value }))
  }

  const handlePhotoClick = () => {
    fileInputRef.current?.click()
  }

  const handlePhotoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file")
        return
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB")
        return
      }

      setSelectedPhoto(file)
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setPhotoPreview(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      // Use FormData to handle both regular fields and file upload
      const formData = new FormData()
      
      // Append regular form fields
      formData.append('first_name', profileData.firstName)
      formData.append('last_name', profileData.lastName)
      formData.append('occupation', profileData.skillOccupation)
      formData.append('contact', profileData.phoneNumber)
      formData.append('bio', profileData.bio)
      
      // Append profile photo if selected
      if (selectedPhoto) {
        formData.append('profile_photo', selectedPhoto)
      }

      const response = await axios.put(`${BASE_URL}/users/profile/`, formData, {
        headers: { 
          Authorization: `Bearer ${accessToken}`,
        },
      })
      
      // Update local state with response data
      if (response.data.profile_photo) {
        const photoUrl = response.data.profile_photo.startsWith('http') 
          ? response.data.profile_photo 
          : `${BASE_URL}${response.data.profile_photo}`
        setProfileData(prev => ({ ...prev, profilePhoto: photoUrl }))
        setPhotoPreview(photoUrl)
      }
      setSelectedPhoto(null)
      
      toast.success("Profile updated successfully")
    } catch (error) {
      //console.error("Profile update error:", error)
      toast.error("Failed to update profile")
    }
  }

  return (
    <div className="profile-settings">
      {/* Cover Photo Placeholder */}
      <div className="cover-photo-container mb-4">
        <div className="cover-photo">
          {/*<button className="btn btn-light btn-sm position-absolute top-0 end-0 m-2">
            <i className="fas fa-trash"></i>
          </button>*/}
        </div>
        <div className="profile-photo-container">
          <div className="profile-photo" onClick={handlePhotoClick} style={{ cursor: 'pointer' }}>
            {(photoPreview || profileData.profilePhoto) ? (
              <img 
                src={photoPreview || profileData.profilePhoto} 
                alt="Profile" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 'inherit'
                }}
              />
            ) : (
              <div className="upload-icon">
                <i className="fas fa-camera"></i>
              </div>
            )}
            {/* Overlay icon for existing photos */}
            {(photoPreview || profileData.profilePhoto) && (
              <div 
                className="upload-overlay"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  borderRadius: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.opacity = 1}
                onMouseLeave={(e) => e.target.style.opacity = 0}
              >
                <i className="fas fa-camera" style={{ color: 'white', fontSize: '1.5rem' }}></i>
              </div>
            )}
          </div>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            style={{ display: 'none' }}
          />
        </div>
        <div className="photo-info d-flex justify-content-end mt-2">
          <small className="text-muted me-3">Profile Photo Size: 200x200 pixels</small>
          {selectedPhoto && (
            <small className="text-success">New photo selected</small>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row mb-3">
          <div className="col-md-6 mb-3 mb-md-0">
            <label htmlFor="firstName" className="form-label">First Name</label>
            <input
              type="text"
              className="form-control"
              id="firstName"
              name="firstName"
              value={profileData.firstName}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="lastName" className="form-label">Last Name</label>
            <input
              type="text"
              className="form-control"
              id="lastName"
              name="lastName"
              value={profileData.lastName}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6 mb-3 mb-md-0">
            <label htmlFor="userName" className="form-label">User Name</label>
            <input
              type="text"
              className="form-control"
              id="userName"
              name="userName"
              value={profileData.userName}
              readOnly
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="phoneNumber" className="form-label">Phone Number</label>
            <input
              type="text"
              className="form-control"
              id="phoneNumber"
              name="phoneNumber"
              value={profileData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Phone Number"
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6 mb-3 mb-md-0">
            <label htmlFor="skillOccupation" className="form-label">Skill/Occupation</label>
            <input
              type="text"
              className="form-control"
              id="skillOccupation"
              name="skillOccupation"
              value={profileData.skillOccupation}
              onChange={handleInputChange}
              placeholder="UX Designer"
            />
          </div>
         {/*} <div className="col-md-6">
            <label htmlFor="timezone" className="form-label">Timezone</label>
            <select
              className="form-select"
              id="timezone"
              name="timezone"
              value={profileData.timezone}
              onChange={handleInputChange}
            >
              <option value="Karachi">Karachi</option>
              <option value="UTC">UTC</option>
              <option value="GMT">GMT</option>
              <option value="EST">EST</option>
              <option value="PST">PST</option>
            </select>
          </div>*/}
        </div>

        <div className="mb-3">
          <label htmlFor="bio" className="form-label">Bio</label>
          <textarea
            className="form-control"
            id="bio"
            name="bio"
            rows="5"
            value={profileData.bio}
            onChange={handleInputChange}
          ></textarea>
        </div>

        {/*<div className="mb-3">
          <label htmlFor="displayName" className="form-label">Display name publicly as</label>
          <select
            className="form-select"
            id="displayName"
            name="displayName"
            value={profileData.displayName}
            onChange={handleInputChange}
          >
            <option value={`${profileData.firstName} ${profileData.lastName}`}>{`${profileData.firstName} ${profileData.lastName}`}</option>
            <option value={profileData.firstName}>{profileData.firstName}</option>
            <option value={profileData.lastName}>{profileData.lastName}</option>
            <option value={profileData.userName}>{profileData.userName}</option>
          </select>
          <div className="form-text mt-2">
            The name is shown in all public fields, such as the author name, instructor name, student name, and name that will be printed on the certificate
          </div>
        </div>*/}

        <button type="submit" className="btn btn-warning">
          {selectedPhoto ? "Update Profile & Photo" : "Update Profile"}
        </button>
      </form>
      
      <ToastContainer />
    </div>
  )
}

export default ProfileSettings